import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { Reducer } from "redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { sessionReducer } from "./slices/session.slice";
import { authApi } from "./apis/auth.api";
import { restaurantApi } from "./apis/restaurant.api";
import { orderApi } from "./apis/order.api";
import { headerApi } from "./apis/header.api";
import { itemApi } from "./apis/item.api";
import { modifierOptionApi } from "./apis/modifier-option.api";
import { listenerMiddleware } from "./listeners/listenersMiddlesware";

const reducers = {
  session: sessionReducer,
  [authApi.reducerPath]: authApi.reducer,
  [restaurantApi.reducerPath]: restaurantApi.reducer,
  [orderApi.reducerPath]: orderApi.reducer,
  [headerApi.reducerPath]: headerApi.reducer,
  [itemApi.reducerPath]: itemApi.reducer,
  [modifierOptionApi.reducerPath]: modifierOptionApi.reducer,
};

const combinedReducer = combineReducers(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      authApi.middleware,
      restaurantApi.middleware,
      orderApi.middleware,
      headerApi.middleware,
      itemApi.middleware,
      modifierOptionApi.middleware,
      listenerMiddleware.middleware,
    ]);
  },
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof combinedReducer>;
export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export { logout, setRestaurant } from "./slices/session.slice";

export { useLoginMutation } from "./apis/auth.api";

export { useUpdateStatusMutation } from "./apis/restaurant.api";

export {
  useFetchOrdersQuery,
  useFetchOrderDetailsQuery,
  useFetchPastOrdersQuery,
  useAcceptOrderMutation,
  useMakeOrderReadyMutation,
} from "./apis/order.api";

export {
  useGetMyHeadersQuery,
  useUpdateHeaderMutation,
  useToggleHeaderActiveMutation,
} from "./apis/header.api";

export {
  useGetMyItemsQuery,
  useToggleItemStockMutation,
} from "./apis/item.api";

export {
  useGetMyModifierOptionsQuery,
  useToggleModifierOptionStockMutation,
} from "./apis/modifier-option.api";
